import React from 'react';

import { Row, Col } from 'antd';
import { I18n } from '@aws-amplify/core';
import type { NamePath } from 'antd/lib/form/interface';

import { useAutoCalculate } from './hooks';
import { SectionTitle, SectionDescription } from '@/components/views/Report/KimLhc/Components/Shared/styles';
import { PartialResultBadge, ResultBadge, VideoDrawerHeader } from '@/components/views/Report/KimLhc/Components/Shared';
import {
	ClothesFormItem,
	TransferFormItem,
	HoldLoadFormItem,
	HandsPositionFormItem,
	SpatialConditionFormItem,
	AdverseConditionsFormItem
} from './Fields';
import { FieldNamesEnum } from '@/components/views/Report/KimLhc/types';
import { HorizontalDivider } from './Fields/styles';

interface WorkConditionField {
	descriptionKey: string;
	fieldName: string;
	component: React.ComponentType<{ fieldName: NamePath }>;
}

const WORK_CONDITION_FIELDS: WorkConditionField[] = [
	{
		descriptionKey: 'Hand/arm position and movements',
		fieldName: 'work_condition_hands_position',
		component: HandsPositionFormItem
	},
	{
		descriptionKey: 'Force transfer/application',
		fieldName: 'work_condition_transfer',
		component: TransferFormItem
	},
	{
		descriptionKey: 'Adverse environmental conditions',
		fieldName: 'work_condition_adverse',
		component: AdverseConditionsFormItem
	},
	{
		descriptionKey: 'Spatial conditions',
		fieldName: 'work_condition_spatial_condition',
		component: SpatialConditionFormItem
	},
	{
		descriptionKey: 'Clothing',
		fieldName: 'work_condition_clothes',
		component: ClothesFormItem
	},
	{
		descriptionKey: 'Holding/carrying',
		fieldName: 'work_condition_hold_load',
		component: HoldLoadFormItem
	}
];

export function WorkCondition(): JSX.Element {
	const WORK_CONDITION_GROUP = FieldNamesEnum.WORK_CONDITION;

	const { workConditionScore } = useAutoCalculate();

	return (
		<Row gutter={[0, 40]}>
			<Col span={24}>
				<VideoDrawerHeader title={I18n.get('Work Condition')} />
			</Col>

			<Col span={24}>
				<Row gutter={[0, 32]}>
					<Col span={24}>
						<Row align="middle">
							<Col flex="1">
								<SectionTitle level={5}>{I18n.get('Unfavorable work conditions')}</SectionTitle>
							</Col>
							<Col>
								<ResultBadge value={workConditionScore} />
							</Col>
						</Row>
					</Col>
					{WORK_CONDITION_FIELDS.map((field, index) => {
						const FieldComponent = field.component;
						return (
							<Col key={field.fieldName} span={24}> 
								<Col span={24}>
									<Row gutter={[0, 16]}>
										<Col span={24}>
											<SectionDescription>{I18n.get(field.descriptionKey)}</SectionDescription>
										</Col>
										<Col span={24}>
											<FieldComponent fieldName={[WORK_CONDITION_GROUP, field.fieldName]} />
										</Col>
									</Row>
								</Col>
								{index < WORK_CONDITION_FIELDS.length - 1 && (
									<Col span={24}>
										<HorizontalDivider />
									</Col>
								)}
							</Col>
						);
					})}
					<PartialResultBadge />
				</Row>
			</Col>
		</Row>
	);
}
